import { useState } from "react"
import { api, ApiError } from "../../../lib/api"
import { useSelectedAccount } from "../../../contexts/AccountContext"
import Modal from "../../Modal"

type Props = {
  onClose: () => void
  onInvited: (message: string) => void
}

type DeliveryMethod = "email" | "link"

const EXPIRES_OPTIONS = [
  { value: 1, label: "1日" },
  { value: 3, label: "3日" },
  { value: 7, label: "7日" },
  { value: 30, label: "30日" },
]

const ROLE_OPTIONS = [
  { value: "member", label: "メンバー" },
  { value: "owner", label: "オーナー" },
]

const InviteModal = ({ onClose, onInvited }: Props) => {
  const { selectedOrganizationId } = useSelectedAccount()
  const [email, setEmail] = useState("")
  const [role, setRole] = useState("member")
  const [expiresInDays, setExpiresInDays] = useState(7)
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("email")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [clipboardFailUrl, setClipboardFailUrl] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setClipboardFailUrl(null)
    setSubmitting(true)
    try {
      const { invitation } = await api.post<{ message: string; invitation: { token: string; url: string } }>(
        "/invitations",
        { organization_id: selectedOrganizationId, invitation: { email, role, expires_in_days: expiresInDays }, send_email: deliveryMethod === "email" }
      )

      if (deliveryMethod === "link") {
        const url = `${window.location.origin}${invitation.url}`
        try {
          await navigator.clipboard.writeText(url)
          onInvited("リンクをコピーしました")
        } catch {
          setClipboardFailUrl(url)
          setError("クリップボードへのコピーに失敗しました。以下のURLを手動でコピーしてください。")
        }
      } else {
        onInvited("招待メールを送信しました")
      }
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.errors?.join(", ") ?? errData.error ?? "招待に失敗しました")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal title="メンバーを招待" onClose={onClose}>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}

      {clipboardFailUrl && (
        <div className="invite-url-fallback">
          <input
            type="text"
            readOnly
            value={clipboardFailUrl}
            onClick={e => (e.target as HTMLInputElement).select()}
          />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label>送信方法</label>
          <div className="invite-method-options">
            <label className="invite-method-option">
              <input
                type="radio"
                name="deliveryMethod"
                value="email"
                checked={deliveryMethod === "email"}
                onChange={() => setDeliveryMethod("email")}
              />
              <i className="bi bi-envelope" />
              <span>メールで送る</span>
            </label>
            <label className="invite-method-option">
              <input
                type="radio"
                name="deliveryMethod"
                value="link"
                checked={deliveryMethod === "link"}
                onChange={() => setDeliveryMethod("link")}
              />
              <i className="bi bi-link-45deg" />
              <span>リンクをコピー</span>
            </label>
          </div>
        </div>

        <div className="form-field">
          <label>メールアドレス</label>
          <input
            type="email"
            placeholder="invitee@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label>ロール</label>
          <select value={role} onChange={e => setRole(e.target.value)}>
            {ROLE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>有効期限</label>
          <select
            value={expiresInDays}
            onChange={e => setExpiresInDays(Number(e.target.value))}
          >
            {EXPIRES_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="modal__actions">
          <button type="button" className="btn-secondary btn--sm" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" className="btn-primary btn--sm" disabled={submitting}>
            {submitting
              ? (deliveryMethod === "email" ? "送信中..." : "発行中...")
              : (deliveryMethod === "email" ? "招待メールを送信" : "リンクをコピー")
            }
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default InviteModal
